import React, { useState, useEffect } from 'react';
import { ReactComponent as PetAllowed } from 'icons/pet-allowed-icon.svg';
import { ReactComponent as SmokedAllowed } from 'icons/smoked-allowed-icon.svg';
import { ReactComponent as DisabilityAllowed } from 'icons/disability-allowed-icon.svg';
import { ReactComponent as Check } from 'icons/check-icon.svg';
import { InputField, SelectField, PricesInputField } from 'components/Global';

import { useDispatch, useSelector } from 'react-redux';
import { getSubservices } from 'features/subservices/actions';
import { resetSubservicesData } from 'features/subservices/subservicesSlice';
import { getRentalLocationById } from 'features/rental-location/actions';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';
import InputVehicleSuggestion from 'components/Global/InputVehicleSuggestion';
import { getVehicleById, getVehiclesByFilter } from 'features/vehicle/action';
import clsx from 'clsx';

// rules array
const rules = [
  {
    id: 'smoke_allowed',
    icon: <SmokedAllowed />,
    label: 'Boleh merokok',
  },
  {
    id: 'disability_allowed',
    icon: <DisabilityAllowed />,
    label: 'Disabilitas Support',
  },
  {
    id: 'pet_allowed',
    icon: <PetAllowed />,
    label: 'Peliharaan diperbolehkan',
  },
];

// car status array
const status = [
  {
    id: '1',
    name: 'Ready',
  },
  {
    id: '2',
    name: 'In Use',
  },
  {
    id: '3',
    name: 'In Repair',
  },
];

// transmision array
const transmission = [
  {
    id: '1',
    name: 'manual',
  },
  {
    id: '2',
    name: 'matic',
  },
];

const ContentLeft = ({
  dataCar,
  setDataCar,
  isFirstLoad,
  setIsFirstLoad,
  withoutDriver,
  setWithoutDriver,
  withDriver,
  setWithDriver,
  carCharter,
  setCarCharter,
}) => {
  const dispatch = useDispatch();
  // global state
  const brands = useSelector((state) => state.brands?.data);
  const garages = useSelector((state) => state.garages?.data);
  const category = useSelector((state) => state.category.data.category);
  const subservices = useSelector((state) => state.subservices?.data);
  const rentalLocation = useSelector((state) => state.rentalLocation?.selected);
  const dataVehicle = useSelector((state) => state.vehicle?.data);

  // plat number state
  const [codeArea, setCodeArea] = useState('');
  const [carNumber, setCarNumber] = useState('');
  const [codeLetters, setCodeLetters] = useState('');
  // check if using vehicle suggestion
  const [useAutoComplete, setUseAutoComplete] = useState(false);
  // charter
  const [charterPricing, setCharterPricing] = useState([
    {
      id: 'price_charter_half',
      price: 0,
      duration: 4,
      label: 'Half Day',
      title: 'Harga Car Charter - Half Day (4 Jam)',
    },
    {
      id: 'price_charter_full',
      price: 0,
      duration: 8,
      label: 'Full Day',
      title: 'Harga Car Charter - Full Day (8 Jam)',
    },
  ]);

  const isAddVehicleState = location.pathname.includes('add-vehicle');

  const isCarCharterEnabled = useFliptBoolean('FLAG_CHARTER', false, 'anonymous');
  const isHideSlashPriceInput = useFliptBoolean('FLAG_HIDE_VEHICLE_SLASH_PRICE_INPUT', false, 'anonymous');

  // function to handle checkbox rules
  const handleCheck = (e) => {
    setIsFirstLoad(false);
    const { name, checked } = e.target;
    setDataCar({ ...dataCar, [name]: checked });
  };

  // function to handle radio button drivers
  const handleRadio = (e) => {
    setIsFirstLoad(false);
    const { value, checked } = e.target;
    if (value === 'tanpa-supir') {
      setWithoutDriver(checked);
    } else if (value === 'dengan-supir') {
      setWithDriver(checked);
    } else if (isCarCharterEnabled && value === 'charter') {
      setCarCharter(checked);
    }
  };

  const handleChangeGarage = async (e) => {
    setIsFirstLoad(false);

    setWithoutDriver(false);
    setWithDriver(false);
    if (isCarCharterEnabled) setCarCharter(false);

    setDataCar({
      ...dataCar,
      garage_vehicles: Number.parseInt(e.target.value),

      price_with_driver: '',
      price: '',
      ...(isCarCharterEnabled && {
        price_charter_half: '',
        price_charter_full: '',
        charter_pricing: [],
      }),
    });

    if (!dataCar.name?.trim()) return;

    const getVehicleByLocation = await dispatch(
      getVehiclesByFilter({
        name: dataCar.name?.toLowerCase() || '',
        locationRental: getLocationIdByGarage(Number.parseInt(e.target.value)),
        limit: 0,
        page: 0,
      }),
    ).unwrap();

    if (getVehicleByLocation?.vehicles?.length > 0 && dataCar.name?.trim() && isAddVehicleState) {
      const getSameVehicleName = dataVehicle?.vehicles?.filter(
        (item) => item.name?.toLowerCase() === dataCar.name?.toLowerCase().trim(),
      );

      if (!getSameVehicleName?.length) return;

      if (dataCar.name?.toLowerCase()?.trim() === getSameVehicleName?.[0]?.name?.toLowerCase()) {
        setUseAutoComplete(true);
      } else {
        setUseAutoComplete(false);
      }
    }
  };

  // function to handle input prices
  const handleChangePrice = (e) => {
    setIsFirstLoad(false);
    const { name, value } = e.target;
    setDataCar({ ...dataCar, [name]: value });
  };

  // function to prevent scroll behavior input number
  const numberInputOnWheelPreventChange = (e) => {
    // Prevent the input value change
    e.target.blur();

    // Prevent the page/container scrolling
    e.stopPropagation();

    // Refocus immediately, on the next tick (after the current
    // function is done)
    setTimeout(() => {
      e.target.focus();
    }, 0);
  };

  const handleClickCarSuggestion = async (vehicleId) => {
    setIsFirstLoad(false);

    const getVehicleData = await dispatch(getVehicleById(vehicleId)).unwrap();

    if (getVehicleData) {
      const {
        brand_vehicles,
        name,
        year,
        category,
        smoke_allowed,
        pet_allowed,
        disability_allowed,
        required_driver,
        support_driver,
        price,
        price_with_driver,
        slash_price,
        status,
        max_passanger,
        max_suitcase,
        transmission,
        garage_vehicles,
        photo,
      } = getVehicleData;

      setDataCar({
        ...dataCar,
        brand_vehicles,
        name,
        year,
        category_vehicles: category.id,
        smoke_allowed,
        pet_allowed,
        disability_allowed,
        required_driver,
        support_driver,
        price,
        price_with_driver,
        slash_price,
        status,
        max_passanger,
        max_suitcase,
        transmission,
        garage_vehicles,
        photo,
        ...(isCarCharterEnabled && {
          charter_pricing: getVehicleData.charter_pricing,
          support_charter: getVehicleData.support_charter,
          price_charter_half: getVehicleData.charter_pricing?.find((item) => item.label.toLowerCase().includes('half'))
            ?.price,
          price_charter_full: getVehicleData.charter_pricing?.find((item) => item.label.toLowerCase().includes('full'))
            ?.price,
        }),
      });

      if (isAddVehicleState) {
        setUseAutoComplete(true);
      }

      checkingVehicleService(getVehicleData);
    }
  };

  const checkingVehicleService = (vehicleData) => {
    if (isCarCharterEnabled && vehicleData.support_charter) {
      setCarCharter(true);
      setWithDriver(false);
      setWithoutDriver(false);
      return;
    }

    if (vehicleData.support_driver && vehicleData.required_driver) {
      setWithDriver(true);
      setWithoutDriver(false);
      return;
    }

    if (!vehicleData.support_driver && !vehicleData.required_driver) {
      setWithDriver(false);
      setWithoutDriver(true);
      return;
    }

    if (vehicleData.support_driver && !vehicleData.required_driver) {
      setWithDriver(true);
      setWithoutDriver(true);
    }
  };

  const handleChangeCarName = (e) => {
    const nameValue = e.target.value;

    const getSameVehicleName = dataVehicle?.vehicles?.filter(
      (item) => item.name?.toLowerCase() === nameValue?.toLowerCase().trim(),
    )?.[0];

    if (isAddVehicleState) {
      if (getSameVehicleName) {
        setUseAutoComplete(true);
        setDataCar({
          ...dataCar,
          ...getSameVehicleName,
          name: nameValue,
          category_vehicles: getSameVehicleName?.category?.id,
          photo: [],
          license_number: '',
        });
        checkingVehicleService(getSameVehicleName);
        return;
      } else {
        setUseAutoComplete(false);
      }
    }

    setDataCar({ ...dataCar, name: nameValue });
  };

  const handleChangeStatus = (e) => {
    const selectedStatus = e.target.value;

    let statusName = '';

    if (selectedStatus === '1') {
      statusName = 'available';
    } else if (selectedStatus === '2') {
      statusName = 'booked';
    } else {
      statusName = 'locked';
    }
    setDataCar({
      ...dataCar,
      status: statusName,
    });
  };

  const getCarStatusValue = (status) => {
    if (status === 'available') return 1;
    else if (status === 'booked') return 2;
    else return 3;
  };

  const getCheckedValue = (id) => {
    if (id === 'tanpa-supir') return withoutDriver;
    else if (id === 'dengan-supir') return withDriver;
    else if (isCarCharterEnabled && id === 'charter') return carCharter;
  };

  // will set a licence number when code area, car number, and code letter changing
  useEffect(() => {
    setDataCar({ ...dataCar, license_number: `${codeArea} ${carNumber} ${codeLetters}` });
  }, [codeArea, carNumber, codeLetters]);

  // set inital value codearea car number code letter
  useEffect(() => {
    if (dataCar.license_number) {
      const splittedDataCar = dataCar.license_number.split(' ');
      setCodeArea(splittedDataCar[0]);
      setCarNumber(splittedDataCar[1]);
      setCodeLetters(splittedDataCar[2]);
    }
  }, [dataCar]);

  // will set a new total price and only calulate price & gasoline when with drivers was clicked
  useEffect(() => {
    if (isFirstLoad) return;

    if (!withDriver && withoutDriver) {
      setDataCar({
        ...dataCar,
        price_with_driver: 0,
        support_driver: false,
        required_driver: false,
        ...(isCarCharterEnabled && {
          support_charter: false,
        }),
      });
    }

    if (withDriver && withoutDriver) {
      setDataCar({
        ...dataCar,
        support_driver: true,
        required_driver: false,
        ...(isCarCharterEnabled && {
          support_charter: false,
        }),
      });
    }

    if (withDriver && !withoutDriver) {
      setDataCar({
        ...dataCar,
        price: 0,
        support_driver: true,
        required_driver: true,
        ...(isCarCharterEnabled && {
          support_charter: false,
        }),
      });
    }

    if (isCarCharterEnabled && carCharter) {
      setDataCar({
        ...dataCar,
        price: 0,
        price_with_driver: 0,
        support_charter: true,
        support_driver: false,
        required_driver: false,
      });
    }
  }, [withDriver, withoutDriver, carCharter, isCarCharterEnabled]);

  useEffect(() => {
    if (isCarCharterEnabled) {
      if (!dataCar?.charter_pricing?.length) return;

      const newData = [...charterPricing].map((data) => {
        const foundData = dataCar.charter_pricing.find((item) => item.duration === data.duration);
        if (foundData) {
          return { ...data, label: foundData.label, price: foundData.price };
        }
        return data;
      });

      setCharterPricing(newData);
    }
  }, [dataCar, isCarCharterEnabled]);

  const getLocationIdByGarage = (garageId) => {
    const selectedGarage = garages.filter((garage) => garage.id === garageId);
    return selectedGarage[0]?.location_id;
  };

  useEffect(() => {
    if (!dataCar.garage_vehicles) return;
    if (garages.length === 0) return;

    const locId = getLocationIdByGarage(dataCar.garage_vehicles);

    dispatch(getSubservices(locId));
    dispatch(getRentalLocationById(locId));

    return () => dispatch(resetSubservicesData());
  }, [dataCar.garage_vehicles, garages]);

  let driversAvailability = [];

  let hasWithoutDriver = false;
  let hasWithDriver = false;
  let hasCarCharter = false;

  for (const item of subservices?.[0]?.facilities || []) {
    if (item.name === 'Without Driver') {
      driversAvailability.push({
        id: 'tanpa-supir',
        label: 'Tanpa Supir',
        checked: true,
      });
      hasWithoutDriver = true;
    } else if (item.name === 'With Driver') {
      driversAvailability.push({
        id: 'dengan-supir',
        label: 'Dengan Supir',
        checked: false,
      });
      hasWithDriver = true;
    } else if (isCarCharterEnabled && item.name === 'Charter') {
      driversAvailability.push({
        id: 'charter',
        label: 'Car Charter',
        checked: false,
      });
      hasCarCharter = true;
    }
  }

  return (
    <div className="content--left">
      {/* GARAGE */}
      <div className="first-section__garage">
        <SelectField
          label="Pilih Garasi"
          type="text"
          data={garages}
          value={dataCar.garage_vehicles}
          htmlFor="garasi-mobil"
          name="garasi-mobil"
          placeholder="Pilih Garasi"
          onChange={handleChangeGarage}
          disabled={useAutoComplete}
        />
      </div>
      <div className="first-section">
        <SelectField
          label="Merk Mobil"
          type="text"
          data={brands}
          name="merk-mobil"
          value={dataCar.brand_vehicles}
          htmlFor="merk-mobil"
          placeholder="Pilih Merk Mobil"
          onChange={(e) => setDataCar({ ...dataCar, brand_vehicles: Number.parseInt(e.target.value) })}
        />
        <InputVehicleSuggestion
          label="Nama Mobil"
          htmlFor="nama-mobil"
          placeholder="Contoh : Xpander"
          onClick={handleClickCarSuggestion}
          value={dataCar.name || ''}
          onChange={handleChangeCarName}
          payloadFilter={{
            limit: 0,
            page: 0,
            locationRental: getLocationIdByGarage(dataCar.garage_vehicles),
          }}
        />

        <InputField
          type="number"
          label="Tahun Mobil"
          htmlFor="tahun-mobil"
          placeholder="Tulis Tahun Mobil"
          value={dataCar.year || ''}
          onChange={(e) => {
            if (e.target.value.length > 4) return;
            setDataCar({ ...dataCar, year: e.target.value });
          }}
          onWheel={numberInputOnWheelPreventChange}
        />
        <SelectField
          label="Kategori Mobil"
          htmlFor="categori-mobil"
          placeholder="Pilih Kategori"
          data={category}
          name="category-mobil"
          value={dataCar.category_vehicles}
          onChange={(e) =>
            setDataCar({
              ...dataCar,
              category_vehicles: e.target.value,
            })
          }
          disable={useAutoComplete}
        />
      </div>

      {/* SECOND SECTION */}
      <div className="second-section">
        {/* RULES */}
        <div className="second-section__rules">
          {rules.map((item) => (
            <div className="checkbox-input" key={item.id}>
              <div className="checkbox-input__box">
                <input type="checkbox" name={item.id} id={item.id} onChange={handleCheck} checked={dataCar[item.id]} />
                <div className={item.id && dataCar[item.id] ? 'checkmark show' : 'checkmark'}>
                  <Check />
                </div>
              </div>
              <label htmlFor={item.id} className="checkbox-input__label">
                {item.icon}
                {item.label}
              </label>
            </div>
          ))}
        </div>

        {/* AVAILABILITY OF DRIVERS */}
        <div className="second-section__drivers-availability">
          {driversAvailability.map((item) => (
            <div key={item.id} className="radio-input">
              <div className="radio-input__box">
                <input
                  type="checkbox"
                  id={item.id}
                  value={item.id}
                  onChange={handleRadio}
                  checked={getCheckedValue(item.id)}
                  disabled={useAutoComplete}
                />
                <div
                  className={clsx(
                    'checkmark',
                    (item.id === 'tanpa-supir' && withoutDriver) ||
                      (item.id === 'dengan-supir' && withDriver) ||
                      (item.id === 'charter' && carCharter)
                      ? 'show'
                      : '',
                  )}
                >
                  <Check />
                </div>
              </div>
              <label htmlFor={item.id}>{item.label}</label>
            </div>
          ))}
        </div>

        {/* PRICES */}
        <div className="second-section__prices">
          {hasWithoutDriver && (
            <PricesInputField
              label="Harga Mobil Tanpa Supir"
              id="price"
              currency={rentalLocation?.currency || ''}
              value={dataCar.price || ''}
              handleChange={handleChangePrice}
              disable={!withoutDriver || useAutoComplete}
              onWheel={numberInputOnWheelPreventChange}
            />
          )}

          {hasWithDriver && (
            <PricesInputField
              label="Harga Mobil Dengan Supir"
              id="price_with_driver"
              currency={rentalLocation?.currency || ''}
              value={dataCar.price_with_driver || ''}
              handleChange={handleChangePrice}
              disable={!withDriver || useAutoComplete}
              onWheel={numberInputOnWheelPreventChange}
            />
          )}

          {isCarCharterEnabled && hasCarCharter && (
            <>
              {charterPricing.map((item) => (
                <PricesInputField
                  key={item.id}
                  label={item.title}
                  id={item.id}
                  currency={rentalLocation?.currency || ''}
                  value={dataCar[item.id] || ''}
                  handleChange={handleChangePrice}
                  disable={!carCharter || useAutoComplete}
                  onWheel={numberInputOnWheelPreventChange}
                />
              ))}
            </>
          )}

          {isHideSlashPriceInput ? null : (
            <PricesInputField
              label="Slash Price"
              id="slash_price"
              currency={rentalLocation?.currency || ''}
              value={dataCar.slash_price || ''}
              handleChange={handleChangePrice}
              onWheel={numberInputOnWheelPreventChange}
              disable={useAutoComplete}
            />
          )}
        </div>
      </div>

      {/* THIRD SECTION */}
      <div className="third-section">
        {/* STATUS CAR */}
        <SelectField
          label="Status Mobil"
          type="text"
          data={status}
          htmlFor="status-mobil"
          placeholder="Ready"
          name="status-mobil"
          value={getCarStatusValue(dataCar.status)}
          onChange={handleChangeStatus}
        />

        {/* PLAT NO */}
        <div className="third-section__plat">
          <InputField
            label="No. Plat"
            maxLength="2"
            value={codeArea.toUpperCase() || ''}
            onChange={(e) => setCodeArea(e.target.value)}
            placeholder="Contoh : DK"
          />
          <InputField
            type="number"
            onChange={(e) => {
              if (e.target.value.length > 4) return;
              setCarNumber(e.target.value || '');
            }}
            value={carNumber}
            placeholder="1234"
            onWheel={numberInputOnWheelPreventChange}
          />
          <InputField
            maxLength="3"
            value={codeLetters.toUpperCase() || ''}
            onChange={(e) => setCodeLetters(e.target.value)}
            placeholder="HY"
          />
        </div>

        {/* SEATS & CASE */}
        <div className="third-section__seats">
          <InputField
            label="Jumlah Kursi"
            value={dataCar.max_passanger || ''}
            type="number"
            onChange={(e) => {
              if (e.target.value.length > 2) return;
              setDataCar({ ...dataCar, max_passanger: Number.parseInt(e.target.value) });
            }}
            placeholder="0"
            onWheel={numberInputOnWheelPreventChange}
          />
          <InputField
            label="Maksimal Koper"
            value={dataCar.max_suitcase || ''}
            type="number"
            onChange={(e) => {
              if (e.target.value.length > 2) return;
              setDataCar({ ...dataCar, max_suitcase: Number.parseInt(e.target.value) });
            }}
            placeholder="0"
            onWheel={numberInputOnWheelPreventChange}
          />
        </div>

        {/* TRANSMISSION */}
        <div className="third-section__transmission">
          <SelectField
            label="Transmisi"
            type="text"
            data={transmission}
            htmlFor="transmisi-mobil"
            name="transmisi-mobil"
            value={dataCar.transmission === 'manual' ? 1 : 2}
            placeholder="Manual"
            onChange={(e) => setDataCar({ ...dataCar, transmission: e.target.value == '1' ? 'manual' : 'matic' })}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(ContentLeft);
